/**
 * Use User Management Hook
 *
 * Custom hooks for managing users with TanStack Query.
 * Handles caching, refetching, mutations, and error states automatically.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchUsers,
  fetchUserById,
  updateUser,
  createOloLink,
  fetchPunchhInfo,
  createPunchhAccount,
  regeneratePunchhTokens,
  forceSignOutUser,
  expireUserIdToken,
  type OloPlatform,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  UserListParams,
  UserListResponse,
  UserDetailsResponse,
  UpdateUserRequest,
  UpdateUserResponse,
  OloLinkResponse,
  PunchhAccountInfo,
  CreatePunchhAccountResponse,
  RegeneratePunchhTokensResponse,
  ForceSignOutResponse,
  ExpireIdTokenResponse,
} from '@/types/user-management';

/**
 * Use Users
 * Fetches paginated list of users with filters
 *
 * @param params - Query parameters for filtering and pagination
 * @returns TanStack Query result with users data
 */
export function useUsers(params: UserListParams): UseQueryResult<UserListResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchUsers(params, token);
    },
    staleTime: 30000, // 30 seconds - data is considered fresh for 30s
    gcTime: 300000, // 5 minutes - cache is garbage collected after 5 minutes
    retry: 2, // Retry failed requests twice
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnReconnect: true, // Refetch when network reconnects
  });
}

/**
 * Use User by ID
 * Fetches a single user by their ID with metadata
 *
 * @param userId - User ID
 * @returns TanStack Query result with single user data
 */
export function useUserById(userId: string | null): UseQueryResult<UserDetailsResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error('No user ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchUserById(userId, token);
    },
    enabled: userId !== null, // Only run query if ID is provided
    staleTime: 10000, // 10 seconds - individual users change more frequently
    gcTime: 300000, // 5 minutes - keep in cache
    retry: 2,
  });
}

/**
 * Use Punchh Info
 * Fetches Punchh account information for a user
 *
 * @param userId - User ID
 * @returns TanStack Query result with Punchh account info
 */
export function usePunchhInfo(userId: string | null): UseQueryResult<PunchhAccountInfo> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['punchhInfo', userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error('No user ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchPunchhInfo(userId, token);
    },
    enabled: userId !== null, // Only run query if ID is provided
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    retry: 1, // Punchh data is less critical, only retry once
  });
}

/**
 * Use Update User
 * Mutation hook for updating an existing user
 *
 * @returns TanStack Mutation result
 */
export function useUpdateUser(): UseMutationResult<
  UpdateUserResponse,
  Error,
  { userId: string; data: UpdateUserRequest }
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserRequest }) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return updateUser(userId, data, token);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', data.user.id] });
    },
  });
}

/**
 * Parameters for creating/refreshing Olo SSO link
 */
export interface CreateOloLinkParams {
  userId: string;
  platforms?: OloPlatform[];
}

/**
 * Use Create Olo Link
 * Mutation hook for creating or refreshing Olo SSO link
 * Supports per-platform token regeneration.
 *
 * @returns TanStack Mutation result
 */
export function useCreateOloLink(): UseMutationResult<OloLinkResponse, Error, CreateOloLinkParams> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, platforms }: CreateOloLinkParams) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return createOloLink(userId, token, platforms);
    },
    onSuccess: data => {
      // Invalidate both the list and the specific user query
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', data.user.id] });
    },
  });
}

/**
 * Use Create Punchh Account
 * Mutation hook for creating a new Punchh account
 *
 * @returns TanStack Mutation result
 */
export function useCreatePunchhAccount(): UseMutationResult<
  CreatePunchhAccountResponse,
  Error,
  string
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return createPunchhAccount(userId, token);
    },
    onSuccess: data => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', data.user.id] });
      queryClient.invalidateQueries({ queryKey: ['punchhInfo', data.user.id] });
    },
  });
}

/**
 * Use Regenerate Punchh Tokens
 * Mutation hook for regenerating Punchh auth and access tokens
 *
 * @returns TanStack Mutation result
 */
export function useRegeneratePunchhTokens(): UseMutationResult<
  RegeneratePunchhTokensResponse,
  Error,
  string
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return regeneratePunchhTokens(userId, token);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['user', data.user.id] });
      queryClient.invalidateQueries({ queryKey: ['punchhInfo', data.user.id] });
    },
  });
}

/**
 * Use Force Sign-Out User
 * Mutation hook that calls POST /v1/admin/users/:userId/force-sign-out.
 * Revokes the user's Cognito refresh tokens and writes a local idToken
 * revocation entry so existing sessions stop authenticating.
 */
export function useForceSignOutUser(): UseMutationResult<ForceSignOutResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return forceSignOutUser(userId, token);
    },
    onSuccess: (_data, userId) => {
      // The user object itself is unchanged, but invalidate the detail view
      // so any session-related fields we might display later stay fresh.
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
}

/**
 * Use Expire User ID Token
 * Mutation hook that calls POST /v1/admin/users/:userId/expire-id-token.
 * Writes the backend's idToken revocation entry while leaving the user's
 * Cognito refresh token valid — used to test the FE's silent-refresh
 * flow.
 */
export function useExpireUserIdToken(): UseMutationResult<ExpireIdTokenResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return expireUserIdToken(userId, token);
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
}
